import React, {FC, useCallback, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap-v5'
import {shallowEqual, useSelector} from 'react-redux'
import {KTSVG} from '../../../../_metronic/helpers'
import {RootState} from '../../../../setup'
import {UserRole} from '../../../constants/roles'
import {showErrorAlert} from '../../../helpers/alert'
import {getRemainingDays} from '../../../helpers/FormatDate'
import {UserModel} from '../../../modules/auth/models/UserModel'
import questions from '../../../modules/auth/components/helper/json-data/section1.json'
import {acceptRequest, rejectedRequest} from '../../../services/waitingListService'
import {getTickets} from '../../../services/ticketService'
import {hasRole} from '../../../utils/permissions'
import Pagination from '../../components/Pagination'
import {ApproveModal} from '../modals/ApproveModal'
import {DeclineModal} from '../modals/DeclineModal'
import {TicketModal} from '../modals/TicketModal'
import {TicketList} from './TicketList'

type Props = {
  open: boolean
  onClose: (close: boolean) => void
  dataUser?: any
}

const DetailWaitingListModal: FC<Props> = ({open, onClose, dataUser}) => {
  const [showModal, setShowModal] = useState(false)
  const [showDeclineModal, setShowDeclineModal] = useState(false)
  const [showTicketModal, setShowTicketModal] = useState(false)
  const userAccount = useSelector<RootState>(({auth}) => auth.user, shallowEqual) as UserModel
  const [activeTab, setActiveTab] = useState(1)
  const limit = 10
  const [tickets, setTickets] = useState([])
  const [ticketId, setTicketId] = useState(0)
  const [page, setPage] = useState(1)
  const [count, setCount] = useState(1)
  const totalPages = Math.ceil(count / limit)
  const offset = (page - 1) * limit

  const getListTickets = useCallback(async () => {
    if (!dataUser?.id) {
      setTickets([])
      setCount(0)
      return
    }

    const tickets_ = await getTickets({waiting_list: dataUser.id, limit, offset})
    setTickets(tickets_.results)
    setCount(tickets_.count)
  }, [dataUser?.id, offset])

  useEffect(() => {
    if (open) {
      setActiveTab(1)
      setPage(1)
      setTicketId(0)
    }
  }, [open, dataUser?.id])

  useEffect(() => {
    if (open && activeTab === 2) {
      getListTickets()
    }
  }, [open, activeTab, getListTickets])

  const handleCancel = () => {
    onClose(false)
  }

  const handleAccept = async () => {
    try {
      const response = await acceptRequest(dataUser?.id)
      setShowModal(false)
      onClose(false)
      showErrorAlert({title: 'Success', message: response?.data?.success ?? 'User approved'})
    } catch (error: any) {
      showErrorAlert({title: 'Success', message: 'User approved'})
      setShowModal(false)
      onClose(false)
    }
  }

  const handleDecline = async (reason: string, note: string) => {
    try {
      const response = await rejectedRequest(dataUser?.id, {
        rejection_reason_id: reason ?? '',
        notes: note ?? '',
      })
      setShowDeclineModal(false)
      onClose(false)
      showErrorAlert({title: 'Success', message: response?.success ?? 'User declined'})
    } catch (error: any) {
      showErrorAlert({title: 'Success', message: 'User declined'})
      setShowDeclineModal(false)
      onClose(false)
    }
  }

  const changeTab = (status: number) => {
    setActiveTab(status)
    if (status === 2) {
      setPage(1)
    }
  }

  const handleCloseTicket = () => {
    setTicketId(0)
    setShowTicketModal(false)
    getListTickets()
  }

  const handleSelectTicket = (id: number) => {
    setTicketId(id)
    setShowTicketModal(true)
  }

  const handlePageChange = (page: number) => {
    setPage(page)
  }

  const remainingDays = getRemainingDays(dataUser?.createdAt ?? '')
  // Internal referral (WP-B): flagged by source, or by a present referrer name (`referenced`).
  const isReferral = dataUser?.source === 'referral' || Boolean(dataUser?.referenced)
  const referrerName: string | undefined = dataUser?.referenced || undefined

  return (
    <Modal
      show={open}
      onHide={handleCancel}
      backdrop='static'
      dialogClassName='experience-detail-modal'
      contentClassName='experience-detail-modal-content'
      backdropClassName='experience-detail-backdrop'
      className='w-100'
    >
      <div className='admin-experience-details-container'>
        <div className='rm-style-1'>
          <div className='rm-style-2 text-truncate'>
            Request of Membership - {dataUser?.firstName ?? ''} {dataUser?.lastName ?? ''}
          </div>
          <div className='rm-style-3 cursor-pointer' onClick={handleCancel}>
            <KTSVG path='/media/svg/nobilis/closeX.svg' className='rm-style-4' />
          </div>
        </div>
        <div className='rm-style-7 d-flex flex-wrap flex-md-nowrap'>
          <div className='card col-12 col-md-8'>
            <ul className='nav nav-tabs nav-line-tabs nav-stretch fs-6 border-bottom-0' style={{ overflow: 'auto', flexWrap: 'nowrap' }}>
              <li className='nav-item'>
                <button type='button' className={`nav-link ${activeTab === 1 ? 'active' : ''}`} onClick={() => changeTab(1)}>
                  General Information
                </button>
              </li>
              <li className='separator-vertical'>
                <hr />
              </li>
              <li className='nav-item'>
                <button type='button' className={`nav-link ${activeTab === 2 ? 'active' : ''}`} onClick={() => changeTab(2)}>
                  Support Tickets
                </button>
              </li>
            </ul>
          </div>
          <div className='rm-style-8 cursor-pointer col-12 col-md-4' onClick={() => setShowTicketModal(true)}>
            <div data-property-1='calendar-edit' className='rm-style-9'>
              <KTSVG path='/media/svg/nobilis/calendar-edit.svg' className='rm-style-5' />
            </div>
            <div className='rm-style-13'>Create ticket</div>
          </div>
        </div>
        <div className={`rm-style-14 ${activeTab === 1 ? 'd-flex' : 'd-none'}`}>
          <div className='rm-style-23'>
            <div className='rm-style-24'>
              <div className='rm-style-16'>
                <div className='rm-style-17 col-12'>
                  <div className='rm-style-18'>Assign Admin</div>
                  <div className='rm-style-19'>
                    {userAccount?.firstName} {userAccount?.lastName}
                  </div>
                </div>
              </div>
              <div className='rm-style-25'>
                <div className='d-flex flex-row w-100'>
                  <div className='rm-style-26 justify-content-start w-50'>#{dataUser?.id ?? ''}</div>
                  <div className='rm-style-36 justify-content-end w-50'></div>
                </div>
                <div className='rm-style-27'>
                  {dataUser?.firstName ?? ''} {dataUser?.lastName ?? ''}
                </div>
              </div>
              <div className='rm-style-28 d-flex flex-wrap flex-md-nowrap'>
                <div className='rm-style-29 col-12 col-md-6'>
                  <div className='rm-style-30'>Occupation </div>
                  <div className='rm-style-31'>{dataUser?.occupation || 'No occupation provided.'}</div>
                </div>
                <div className='rm-style-32 col-12 col-md-6'>
                  <div className='rm-style-33'>City</div>
                  <div className='rm-style-34'>{dataUser?.city || 'No location provided.'}</div>
                </div>
              </div>
              <div className='rm-style-38 d-flex flex-wrap flex-md-nowrap'>
                <div className='rm-style-39 col-12 col-md-6'>
                  <div className='rm-style-40'>Email</div>
                  <div className='rm-style-41'>{dataUser?.email ?? ''}</div>
                </div>
                <div className='rm-style-42 col-12 col-md-6'>
                  <div className='rm-style-43'>Phone</div>
                  <div className='rm-style-44'>{dataUser?.phoneNumber ?? ''}</div>
                </div>
              </div>
              <div className='rm-style-38 d-flex flex-wrap flex-md-nowrap'>
                <div className='rm-style-45 col-12 col-md-6'>
                  <div className='rm-style-46'>Requested on</div>
                  <div className='rm-style-47'>
                    <div className='rm-style-48'>{dataUser?.createdAt ?? ''}</div>
                    <div className='rm-style-49'>
                      {remainingDays} Days {remainingDays > 0 ? 'days left' : 'overdue'}
                    </div>
                  </div>
                </div>
                <div className='rm-style-50 col-12 col-md-6'>
                  <div className='rm-style-51'>
                    Invited by :
                    {isReferral && (
                      <span className='badge badge-light-primary ms-2'>Internal referral</span>
                    )}
                  </div>
                  <div data-property-1='small' className='rm-style-52'>
                    {dataUser?.referenced !== '' ? <img className='rm-style-53' src='https://placehold.co/28x28' alt='' /> : null}
                    <div className='rm-style-54'>{dataUser?.referenced || 'N/A'}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='rm-style-55'>
            <div className='rm-style-56'>
              <div className='rm-style-57'>Category</div>
              <div className='rm-style-58'>
                {(questions?.[2]?.section03 ?? []).map((option, index) => {
                  return dataUser?.[option.code] === true ? (
                    <div key={`category-${index}`} className='rm-style-59'>
                      <div className='rm-style-60'>
                        <div className='rm-style-61'>{option?.title} </div>
                        <div className='rm-style-62'>{option?.description}</div>
                      </div>
                    </div>
                  ) : null
                })}
              </div>
              <div className='rm-style-67'>
                <div className='rm-style-68'>Net Income: </div>
                <div className='rm-style-69'>{dataUser?.incomeRange || ''}</div>
              </div>
              <div className='rm-style-67'>
                <div className='rm-style-68'>Initiation fee: </div>
                <div className='rm-style-69'>
                  {(questions?.[2]?.section03 ?? []).map((option) => {
                    return dataUser?.[option.code] === true ? option?.description : null
                  })}
                </div>
              </div>
            </div>
            <div className='rm-style-70'>
              <div className='rm-style-71'>Source</div>
              <div className='rm-style-72'>
                {dataUser?.linkVerify ? (
                  <a href={dataUser?.linkVerify} target='_blank' rel='noreferrer'>
                    {dataUser?.linkVerify}
                  </a>
                ) : (
                  'N/A'
                )}
              </div>
            </div>
          </div>
          <div className='rm-style-73'>
            <div className='rm-style-74'>
              <div className='rm-style-75'>Interest</div>
              <div className='rm-style-76'>
                {questions?.[0]?.section01 && questions?.[0]?.section01.length > 0 ? (
                  questions?.[0]?.section01.map((option, index) => {
                    return dataUser?.[option.code] === true ? (
                      <div key={`interest-${index}`} className='rm-style-77'>
                        <div className='rm-style-78'>{option?.description}</div>
                      </div>
                    ) : null
                  })
                ) : (
                  <div key='interest-0' className='rm-style-77'>
                    <div className='rm-style-78'>No interests selected yet.</div>
                  </div>
                )}
              </div>
            </div>
            <div className='rm-style-81'>
              <div className='rm-style-82'>Intentions</div>
              <div className='rm-style-83'>
                {questions?.[1]?.section02 && questions?.[1]?.section02.length > 0 ? (
                  (questions?.[1]?.section02 ?? []).map((option, index) => {
                    return dataUser?.[option.code] === true ? (
                      <div key={`intentions-${index}`} className='rm-style-77'>
                        <div className='rm-style-78'>{option?.description}</div>
                      </div>
                    ) : null
                  })
                ) : (
                  <div key='intentions-0' className='rm-style-77'>
                    <div className='rm-style-78'>No intentions selected yet.</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className={`rm-style-88 d-flex flex-wrap flex-md-nowrap ${activeTab === 1 ? 'd-flex' : 'd-none'}`}>
          <div
            data-type='second'
            className={`rm-style-89 col-12 col-md-4 ${dataUser?.status === 'pending' ? 'd-flex' : 'd-none'}`}
            onClick={() => setShowDeclineModal(true)}
            role='button'
          >
            <div className='rm-style-90'>Decline Request</div>
          </div>
          <div
            data-type='main'
            className={`rm-style-91 col-12 col-md-5 ${dataUser?.status === 'pending' ? 'd-flex' : 'd-none'}`}
            onClick={() => setShowModal(true)}
            role='button'
          >
            <div className='rm-style-92'>Approve Request</div>
            <div className='rm-style-93'>
              <KTSVG path='/media/svg/nobilis/arrow-right-white.svg' />
            </div>
          </div>
        </div>
        <div className={`rm-style-88 flex-column ${activeTab === 2 ? 'd-flex' : 'd-none'}`}>
          <div className='rm-style-57'>Tickets</div>
          {tickets && tickets.length > 0 && (
            <>
              <TicketList data={tickets} userId={dataUser?.id} select={handleSelectTicket} />
              <Pagination currentPage={page} totalPages={totalPages} onPageChange={handlePageChange} />
            </>
          )}
          {tickets && tickets.length === 0 && 'No support tickets available.'}
        </div>
      </div>
      {hasRole(userAccount, UserRole.ADMIN) && (
        <ApproveModal
          show={showModal}
          onHide={() => setShowModal(false)}
          onApprove={handleAccept}
          isReferral={isReferral}
          referrerName={referrerName}
        />
      )}
      {hasRole(userAccount, UserRole.ADMIN) && (
        <DeclineModal show={showDeclineModal} onHide={() => setShowDeclineModal(false)} onDecline={handleDecline} />
      )}
      <TicketModal show={showTicketModal} onHide={handleCloseTicket} ticketId={ticketId} userId={dataUser?.id || 0} />
    </Modal>
  )
}

export {DetailWaitingListModal}
